import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";

const BackendURL = config.backendUrl;

/* -----------------------------------------
    1️⃣ PROJECT LEVEL READ / UNREAD
-----------------------------------------*/

// Set Project isRead = true
export const setProjectReadTrue = createAsyncThunk(
    "read/setProjectReadTrue",
    async (projectId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(
                `${BackendURL}/read/project/true/${projectId}`
            );
            return res.data.project;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Set Project isRead = false
export const setProjectReadFalse = createAsyncThunk(
    "read/setProjectReadFalse",
    async (projectId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(
                `${BackendURL}/read/project/false/${projectId}`
            );
            return res.data.project;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/* -----------------------------------------
    2️⃣ PERSON LEVEL READ / UNREAD
-----------------------------------------*/

// Set Person isRead = true
export const setPersonReadTrue = createAsyncThunk(
    "read/setPersonReadTrue",
    async ({ projectId, personId }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(
                `${BackendURL}/read/person/true/${projectId}/${personId}`
            );
            return res.data.project;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Set Person isRead = false
export const setPersonReadFalse = createAsyncThunk(
    "read/setPersonReadFalse",
    async ({ projectId, personId }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(
                `${BackendURL}/read/person/false/${projectId}/${personId}`
            );
            return res.data.project;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/* -----------------------------------------
    3️⃣ LEAD LEVEL READ / UNREAD
-----------------------------------------*/

// Set Lead isRead = true
export const setLeadReadTrue = createAsyncThunk(
    "read/setLeadReadTrue",
    async (quotationId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(
                `${BackendURL}/read/lead/true/${quotationId}`
            );
            return res.data.lead;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Set Lead isRead = false
export const setLeadReadFalse = createAsyncThunk(
    "read/setLeadReadFalse",
    async (quotationId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(
                `${BackendURL}/read/lead/false/${quotationId}`
            );
            return res.data.lead;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/* -----------------------------------------
    SLICE
-----------------------------------------*/

const readSlice = createSlice({
    name: "read",
    initialState: {
        loading: false,
        error: null,

        projectStatus: null,
        personStatus: null,
        leadStatus: null,
    },

    reducers: {
        clearReadState: (state) => {
            state.loading = false;
            state.error = null;
        },
    },

    extraReducers: (builder) => {
        builder
            /* ----------------- LOADING ----------------- */
            .addMatcher(
                (action) => action.type.startsWith("read/") && action.type.endsWith("/pending"),
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )

            /* ----------------- FULFILLED ----------------- */
            .addMatcher(
                (action) => action.type.startsWith("read/") && action.type.endsWith("/fulfilled"),
                (state, { payload }) => {
                    state.loading = false;

                    // Auto-detect if it's project / person / lead
                    if (payload?.Assignedperson) state.projectStatus = payload; // Project
                    else if (payload?.ProjectName) state.leadStatus = payload; // Lead
                    else state.personStatus = payload; // Person
                }
            )

            /* ----------------- REJECTED ----------------- */
            .addMatcher(
                (action) => action.type.startsWith("read/") && action.type.endsWith("/rejected"),
                (state, action) => {
                    state.loading = false;
                    state.error = action.payload;
                }
            );
    },
});

export const { clearReadState } = readSlice.actions;

export default readSlice.reducer;
